import { useServicesContext } from "@modules/core/services/services.context"
import { ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"
import { useMutation, useQueryClient } from "@tanstack/react-query"

export const useProgramSasRequestActions = (programId: string) => {
  const { programSasApi } = useServicesContext()
  const queryClient = useQueryClient()

  const { mutateAsync: acceptProgramSasSubscription, isPending: isAcceptProgramSasSubscriptionPending } = useMutation({
    mutationFn: async (subscriptionId: string) => {
      return await programSasApi.acceptProgramSasSubscription(programId, subscriptionId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["programSasRequests", programId],
      })
      queryClient.invalidateQueries({
        queryKey: ["programSasRequestTotal", programId],
      })
      queryClient.invalidateQueries({
        queryKey: ["programSubscribersSearch", programId],
      })
    },
  })

  const { mutateAsync: acceptProgramSasSubscriptions, isPending: isAcceptProgramSasSubscriptionsPending } = useMutation(
    {
      mutationFn: async (subscriptionIds: string[]) => {
        return await programSasApi.acceptProgramSasSubscriptions(programId, subscriptionIds)
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["programSasRequests", programId],
        })
        queryClient.invalidateQueries({
          queryKey: ["programSasRequestTotal", programId],
        })
        queryClient.invalidateQueries({
          queryKey: ["programSubscribersSearch", programId],
        })
      },
    },
  )

  const { mutateAsync: acceptAllProgramSasSubscriptions, isPending: isAcceptAllProgramSasSubscriptionsPending } =
    useMutation({
      mutationFn: async () => {
        return await programSasApi.acceptAllProgramSasSubscriptions(programId)
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["programSasRequests", programId],
        })
        queryClient.invalidateQueries({
          queryKey: ["programSasRequestTotal", programId],
        })
        queryClient.invalidateQueries({
          queryKey: ["programSubscribersSearch", programId],
        })
      },
    })

  const { mutateAsync: rejectProgramSasSubscription, isPending: isRejectProgramSasSubscriptionPending } = useMutation({
    mutationFn: async (subscriptionId: string) => {
      return await programSasApi.rejectProgramSasSubscription(programId, subscriptionId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["programSasRequests", programId, ProgramSubscriptionStatus.PENDING_REQUEST],
      })
      queryClient.invalidateQueries({
        queryKey: ["programSasRequestTotal", programId],
      })
    },
  })

  return {
    acceptProgramSasSubscription,
    isAcceptProgramSasSubscriptionPending,
    acceptProgramSasSubscriptions,
    isAcceptProgramSasSubscriptionsPending,
    acceptAllProgramSasSubscriptions,
    isAcceptAllProgramSasSubscriptionsPending,
    rejectProgramSasSubscription,
    isRejectProgramSasSubscriptionPending,
  }
}
